import { PubSubService } from '@ohif/core';
import { findSeriesId, getSeriesLabels } from '../clients/orthanc';
import { describeError, readBulkDataText } from '../utils';

const EVENTS = {
  SERIES_LABELS_CHANGED: 'event::sycai:SeriesLabelService:seriesLabelsChanged',
} as const;

export type SeriesLabelEventPayloads = {
  [EVENTS.SERIES_LABELS_CHANGED]: { seriesInstanceUid: string };
};

export interface SeriesLabels {
  labels: readonly string[];
  reviewerName: string | null;
}

const REVIEWER_TAG = '00111010';

function toSequence(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is Record<string, unknown> => !!item && typeof item === 'object'
    );
  }

  return value && typeof value === 'object' ? [value as Record<string, unknown>] : [];
}

function referencesSeries(
  instance: Record<string, unknown>,
  seriesInstanceUid: string
): boolean {
  for (const frameOfReference of toSequence(instance.ReferencedFrameOfReferenceSequence)) {
    for (const study of toSequence(frameOfReference.RTReferencedStudySequence)) {
      for (const series of toSequence(study.RTReferencedSeriesSequence)) {
        if (series.SeriesInstanceUID === seriesInstanceUid) {
          return true;
        }
      }
    }
  }

  for (const series of toSequence(instance.ReferencedSeriesSequence)) {
    if (series.SeriesInstanceUID === seriesInstanceUid) {
      return true;
    }
  }

  return false;
}

function findReferencingRtInstance(
  displaySetService: AppTypes.DisplaySetService,
  studyInstanceUid: string,
  seriesInstanceUid: string
): Record<string, unknown> | null {
  const rtDisplaySets = displaySetService
    .getActiveDisplaySets()
    .filter(
      displaySet =>
        displaySet.Modality === 'RTSTRUCT' && displaySet.StudyInstanceUID === studyInstanceUid
    );

  for (const rtDisplaySet of rtDisplaySets) {
    const instance = rtDisplaySet.instance as Record<string, unknown> | undefined;

    if (instance && referencesSeries(instance, seriesInstanceUid)) {
      return instance;
    }
  }

  return null;
}

export class SeriesLabelService extends PubSubService {
  public static readonly EVENTS = EVENTS;

  public static readonly REGISTRATION = {
    name: 'seriesLabelService',
    create: ({ servicesManager }: { servicesManager: AppTypes.ServicesManager }) =>
      new SeriesLabelService(servicesManager),
  };

  private readonly servicesManager: AppTypes.ServicesManager;

  private labels = new Map<string, readonly string[]>();

  private reviewerNames = new Map<string, string>();

  private inFlight = new Map<string, Promise<void>>();

  private generation = 0;

  constructor(servicesManager: AppTypes.ServicesManager) {
    super(EVENTS);
    this.servicesManager = servicesManager;
  }

  public get(seriesInstanceUid: string): SeriesLabels | null {
    const labels = this.labels.get(seriesInstanceUid);

    if (!labels) {
      return null;
    }

    return { labels, reviewerName: this.reviewerNames.get(seriesInstanceUid) ?? null };
  }

  public async request(studyInstanceUid: string, seriesInstanceUid: string): Promise<void> {
    if (this.labels.has(seriesInstanceUid) && this.reviewerNames.has(seriesInstanceUid)) {
      return;
    }

    const inFlight = this.inFlight.get(seriesInstanceUid);

    if (inFlight) {
      return inFlight;
    }

    const request = this.read(studyInstanceUid, seriesInstanceUid, this.generation).finally(() => {
      if (this.inFlight.get(seriesInstanceUid) === request) {
        this.inFlight.delete(seriesInstanceUid);
      }
    });

    this.inFlight.set(seriesInstanceUid, request);

    return request;
  }

  public onModeExit(): void {
    this.generation += 1;
    this.labels.clear();
    this.reviewerNames.clear();
    this.inFlight.clear();
  }

  private async read(
    studyInstanceUid: string,
    seriesInstanceUid: string,
    generation: number
  ): Promise<void> {
    const [labels, reviewerName] = await Promise.all([
      this.labels.has(seriesInstanceUid)
        ? Promise.resolve(undefined)
        : this.fetchLabels(seriesInstanceUid),
      this.reviewerNames.has(seriesInstanceUid)
        ? Promise.resolve(undefined)
        : this.fetchReviewerName(studyInstanceUid, seriesInstanceUid),
    ]);

    if (this.generation !== generation) {
      return;
    }

    if (labels) {
      this.labels.set(seriesInstanceUid, labels);
    }

    if (reviewerName) {
      this.reviewerNames.set(seriesInstanceUid, reviewerName);
    }

    if (labels || reviewerName) {
      this._broadcastEvent(EVENTS.SERIES_LABELS_CHANGED, { seriesInstanceUid });
    }
  }

  private async fetchLabels(seriesInstanceUid: string): Promise<readonly string[] | undefined> {
    try {
      const orthancSeriesId = await findSeriesId(seriesInstanceUid);

      return orthancSeriesId ? await getSeriesLabels(orthancSeriesId) : [];
    } catch (error) {
      console.error(
        `[sycai] reading the labels for series ${seriesInstanceUid} failed ` +
          `${describeError(error)}; the overlay keeps its last state`
      );

      return undefined;
    }
  }

  private async fetchReviewerName(
    studyInstanceUid: string,
    seriesInstanceUid: string
  ): Promise<string | undefined> {
    try {
      const displaySetService = this.servicesManager.services
        .displaySetService as AppTypes.DisplaySetService;

      const rtInstance = findReferencingRtInstance(
        displaySetService,
        studyInstanceUid,
        seriesInstanceUid
      );

      if (!rtInstance) {
        return undefined;
      }

      const reviewerName = await readBulkDataText(rtInstance[REVIEWER_TAG]);

      return reviewerName ?? undefined;
    } catch (error) {
      console.error(
        `[sycai] reading the QA reviewer name for series ${seriesInstanceUid} failed ` +
          `${describeError(error)}; the labels still render`
      );

      return undefined;
    }
  }
}

export interface SeriesLabelService {
  subscribe: (<E extends keyof SeriesLabelEventPayloads>(
    eventName: E,
    callback: (data: SeriesLabelEventPayloads[E]) => void
  ) => { unsubscribe: () => void }) &
    PubSubService['subscribe'];
}
